import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import
{
  getNotifications,
  getUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
  deleteNotification,
} from "../../services/notiService";

export interface INotification
{
  id: string | number;
  title: string;
  message?: string;
  createAt: string | Date;
  read?: boolean;
  tags?: string[];
}

interface INotificationState
{
  type: "success" | "error" | "warning" | "info" | "loading";
  content: string;
  status: boolean;
  total: INotification[];
  totalCount: number;
  unreadCount: number;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
}

const initialState: INotificationState = {
  type: "success",
  content: "",
  status: false,
  total: [],
  totalCount: 0,
  unreadCount: 0,
  loading: false,
  loadingMore: false,
  hasMore: false,
};

// BE may send snake_case → normalize to our shape
const normalize = (n: any): INotification => ({
  id: n?.id,
  title: n?.title,
  message: n?.message,
  tags: Array.isArray(n?.tags) ? n.tags : [],
  createAt: n?.createAt ?? n?.created_at,
  read: n?.read ?? n?.is_read ?? false,
});

// =============== THUNKS ===============
const DEFAULT_LIMIT = 20;

const extractListAndPagination = (res: any) =>
{
  const list = Array.isArray(res?.data?.data)
    ? res.data.data
    : Array.isArray(res?.data)
      ? res.data
      : [];
  const pagination = res?.data?.pagination ?? {};
  return {
    list,
    hasMore: !!pagination.has_more,
    total: Number(pagination.total ?? 0),
  };
};

export const fetchNotificationsAsync = createAsyncThunk(
  "notifications/fetch",
  async (params: { skip?: number; limit?: number; is_read?: boolean } | undefined, { rejectWithValue }) =>
  {
    try
    {
      const res = await getNotifications({
        skip: params?.skip ?? 0,
        limit: params?.limit ?? DEFAULT_LIMIT,
        ...(params?.is_read !== undefined ? { is_read: params.is_read } : {}),
      });
      return extractListAndPagination(res);
    } catch (e: any)
    {
      return rejectWithValue(e?.response?.data ?? { message: e?.message });
    }
  }
);

export const loadMoreNotificationsAsync = createAsyncThunk(
  "notifications/loadMore",
  async (params: { limit?: number } | undefined, { getState, rejectWithValue }) =>
  {
    try
    {
      const state: any = getState();
      const skip = state?.notificationSlice?.total?.length ?? 0;
      const res = await getNotifications({
        skip,
        limit: params?.limit ?? DEFAULT_LIMIT,
      });
      return extractListAndPagination(res);
    } catch (e: any)
    {
      return rejectWithValue(e?.response?.data ?? { message: e?.message });
    }
  }
);

export const fetchUnreadCountAsync = createAsyncThunk(
  "notifications/unreadCount",
  async (_: void, { rejectWithValue }) =>
  {
    try
    {
      const res = await getUnreadNotificationCount();
      const data = res?.data?.data ?? res?.data ?? {};
      return Number(data?.count ?? data?.unread_count ?? data ?? 0);
    } catch (e: any)
    {
      return rejectWithValue(e?.response?.data ?? { message: e?.message });
    }
  }
);

export const markNotificationReadAsync = createAsyncThunk(
  "notifications/markRead",
  async (id: string | number, { rejectWithValue }) =>
  {
    try
    {
      await markNotificationRead(id);
      return id;
    } catch (e: any)
    {
      return rejectWithValue(e?.response?.data ?? { message: e?.message });
    }
  }
);

export const markAllNotificationsReadAsync = createAsyncThunk(
  "notifications/markAllRead",
  async (_: void, { rejectWithValue }) =>
  {
    try
    {
      await markAllNotificationsRead();
      return true;
    } catch (e: any)
    {
      return rejectWithValue(e?.response?.data ?? { message: e?.message });
    }
  }
);

export const deleteNotificationAsync = createAsyncThunk(
  "notifications/delete",
  async (id: string | number, { rejectWithValue }) =>
  {
    try
    {
      await deleteNotification(id);
      return id;
    } catch (e: any)
    {
      return rejectWithValue(e?.response?.data ?? { message: e?.message });
    }
  }
);

// =============== SLICE ===============
export const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    displayNotification: (
      state,
      action: PayloadAction<{
        type: "success" | "error" | "warning" | "info" | "loading";
        content: string;
      }>
    ) =>
    {
      state.type = action?.payload.type;
      state.content = action?.payload.content;
      state.status = !state.status;
    },

    changeTotalNotifications: (state, action: PayloadAction<any[]>) =>
    {
      state.total = (action.payload || []).map(normalize);
    },

    // Local mark-read (kept for optimistic updates)
    markAsRead: (state, action: PayloadAction<string | number>) =>
    {
      const n = state.total.find((x) => x.id === action.payload);
      if (n && !n.read)
      {
        n.read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },

    markAllAsRead: (state) =>
    {
      state.total.forEach((n) => (n.read = true));
      state.unreadCount = 0;
    },

    // Prepend a socket-pushed notification + bump unread count
    addNotification: (state, action: PayloadAction<INotification>) =>
    {
      state.total = [action.payload, ...state.total];
      state.totalCount += 1;
      if (!action.payload.read) state.unreadCount += 1;
    },
  },
  extraReducers: (builder) =>
  {
    builder
      .addCase(fetchNotificationsAsync.pending, (state) =>
      {
        state.loading = true;
      })
      .addCase(fetchNotificationsAsync.fulfilled, (state, action) =>
      {
        const { list, hasMore, total } = action.payload || { list: [], hasMore: false, total: 0 };
        state.total = list.map(normalize);
        state.hasMore = hasMore;
        state.totalCount = total;
        state.loading = false;
      })
      .addCase(fetchNotificationsAsync.rejected, (state) =>
      {
        state.loading = false;
      })
      .addCase(loadMoreNotificationsAsync.pending, (state) =>
      {
        state.loadingMore = true;
      })
      .addCase(loadMoreNotificationsAsync.fulfilled, (state, action) =>
      {
        const { list, hasMore, total } = action.payload || { list: [], hasMore: false, total: 0 };
        // dedupe by id when appending
        const existingIds = new Set(state.total.map((n) => n.id));
        const newOnes = list.map(normalize).filter((n: INotification) => !existingIds.has(n.id));
        state.total = [...state.total, ...newOnes];
        state.hasMore = hasMore;
        state.totalCount = total;
        state.loadingMore = false;
      })
      .addCase(loadMoreNotificationsAsync.rejected, (state) =>
      {
        state.loadingMore = false;
      })
      .addCase(fetchUnreadCountAsync.fulfilled, (state, action) =>
      {
        state.unreadCount = action.payload || 0;
      })
      .addCase(markNotificationReadAsync.fulfilled, (state, action) =>
      {
        const n = state.total.find((x) => x.id === action.payload);
        if (n && !n.read)
        {
          n.read = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markAllNotificationsReadAsync.fulfilled, (state) =>
      {
        state.total.forEach((n) => (n.read = true));
        state.unreadCount = 0;
      })
      .addCase(deleteNotificationAsync.fulfilled, (state, action) =>
      {
        const removed = state.total.find((x) => x.id === action.payload);
        state.total = state.total.filter((x) => x.id !== action.payload);
        if (removed)
        {
          state.totalCount = Math.max(0, state.totalCount - 1);
          if (!removed.read)
          {
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
        }
      });
  },
});

export const {
  displayNotification,
  changeTotalNotifications,
  markAsRead,
  markAllAsRead,
  addNotification,
} = notificationSlice.actions;

export default notificationSlice.reducer;
